import { ATMOSPHERE_PALETTE as P } from '../palette';
import { forestBiome } from '../biomeArchetypes';
import { COIL_WORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/coilWorldBackdrops';
import { OPENING_ROUTE_DEPTH_BACKDROPS, OPENING_ROUTE_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/openingRouteDepthBackdrops';
import { Z_LAYERS } from '../../../game/rendering/Z_LAYERS';

/** Pig Beach — sunny southern shore, the cozy front door. Bright warm-blue sky,
 *  a high sun with a warm halo, soft tropical clouds, twinkling sea-spray motes,
 *  warm sun shafts, a sun-bloom + warm-sand floor glow, aqua sea-shimmer, drifting
 *  sea-spray on a breeze, a faint water-line flicker, shoreline caustics, and a
 *  palm/sand parallax with the dark Coil-Light lighthouse far on the horizon.
 *  The BRIGHTEST beat of the arc — maximum saturation + light + warmth. */
export const PIG_BEACH = forestBiome({
  id: 'pig_beach',
  sky: { from: 0x8fd3f0, to: 0xf5e3b0 },
  celestial: { atX: 0.74, atY: 0.15, color: P.sunHaze, haloColor: 0xffe39a, size: 92, haloScale: 3.2, coreAlpha: 0.88, haloAlpha: 0.24 },
  cloudBands: { count: 4, color: 0xfdfaf0, sizeMin: 360, sizeMax: 680, alphaMin: 0.05, alphaMax: 0.13, driftPxPerSec: 6, bandYMin: 0.06, bandYMax: 0.30 },
  motes: { colors: [0xeaf7ff, P.crystal, P.sunHaze], textureKey: 'sparkle', driftPx: 44 },
  rays: { color: P.sunHaze, tiltDeg: 10, alphaMin: 0.10, alphaMax: 0.32 },
  glow: {
    blobs: [
      { color: P.sunHaze, atX: 0.74, atY: 0.13, size: 460, alpha: 0.09 },
      { color: 0xf2dca0, atX: 0.5, atY: 0.97, size: 640, alpha: 0.09 },
    ],
    breathe: { periodMs: 6500, min: 0.85, max: 1.2 },
  },
  lightField: { palette: [P.deepTeal, P.crystal, P.sunHaze] },
  weather: { mode: 'fall', color: 0xeaf7ff, colorEnd: 0xc9e8f0, lifespanMs: 5000, windX: 52, gust: { periodMs: 7500, min: 0.5, max: 1.45, phase: 0.3 } },
  flicker: { color: 0xaee7f0, baseAlpha: 0.06, flickerAmp: 0.05 },
  extraLayers: [
    // One additional draw-once plane supplies height behind the ferry cliff.
    // Inserting it here leaves all three existing parallax depths and the
    // behindTile Coil Light mounting unchanged; it cannot cover that landmark.
    { kind: 'parallax', shape: 'peaks', color: 0x6f9e86, speed: 0.025,
      height: OPENING_ROUTE_DEPTH_HEIGHTS.pigBeach, alpha: 0.28, y: 0, depth: Z_LAYERS.PARALLAX_FAR - 0.5,
      svg: OPENING_ROUTE_DEPTH_BACKDROPS.pigBeach },
    // Shoreline caustics — moving water light along the beach band.
    { kind: 'caustics', color: 0xbdeeff, count: 6, sizeMin: 220, sizeMax: 420, alphaMin: 0.07, alphaMax: 0.24, bandYMin: 0.74, bandYMax: 0.95, driftPx: 40, periodMinMs: 3000, periodMaxMs: 6500 },
    // Distant gulls — slow critters traversing high in the sky.
    { kind: 'critters', count: 4, palette: [0xf0f0f0, 0xe0e0e0], sizeMin: 4, sizeMax: 7, speedMin: 10, speedMax: 18, bandYMin: 0.08, bandYMax: 0.22, direction: 'both' },
  ],
  parallax: [
    // Authored silhouettes (Coil art pack): soft headland with the Coil Light as
    // a thin cool-dark spire rising behind the far ridge (the through-line's
    // first appearance), palm-topped mid ridge, warm dune near ridge.
    { shape: 'peaks', color: 0x6f9e86, speed: 0.04, height: 320, alpha: 0.66, detail: 0.7, sway: { ampPx: 10, periodMs: 28000 }, svg: COIL_WORLD_BACKDROPS.beach.far },
    { shape: 'peaks', color: 0x4a7d5e, speed: 0.10, height: 240, alpha: 0.82, detail: 1.0, svg: COIL_WORLD_BACKDROPS.beach.mid },
    { shape: 'ridge', color: 0xd6b274, speed: 0.18, height: 150, alpha: 0.92, detail: 1.1, svg: COIL_WORLD_BACKDROPS.beach.near },
  ],
  closer: {
    vignette: { color: 0x2a4a5c, edgeAlpha: 0.18, washColor: 0x8fd0ec, washAlpha: 0.04 },
    grade: { top: 0xffe6a8, bottom: 0x8fcbe4, strength: 0.10, blend: 'normal' },
  },
});
